import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import Form from "react-bootstrap/Form";
import Button from "@mui/material/Button";
import { Formik } from "formik";
import * as yup from "yup";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelIcon from "@mui/icons-material/Cancel";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { masterHandler, customerHandler } from "../../../redux/handlers";
import {
  ChangeEmailModal,
  ResetPasswordModal,
  ChangeEmailSuccessModal,
  ResetPasswordSuccessModal,
} from "../modals/GeneralInfoModal";

const TEXTFIELD_COMMON_PROPS = {
  InputProps: { sx: { borderRadius: "7px" } },
  sx: {
    ".Mui-disabled": {
      background: "#F8F8F9",
    },
  },
  fullWidth: true,
};

const BUTTON_STYLES = {
  edit: "!bg-[#409FFB] !rounded-lg !text-white",
  cancel: "!border-[#6D788D] !rounded-lg !text-[#6D788D]",
  changeEmail: "!bg-[#FF6B00] !rounded-lg !text-white px-4",
  resetPassword: "!bg-[#409FFB] !rounded-lg !text-white px-4",
};

const INITIAL_FORM_DATA = {
  prefix: "",
  customerName: "",
  customerSurname: "",
  customerPhoneNumber: "",
  customerGroup: "",
};

const INITIAL_ACCOUNT_DATA = {
  email: "",
  password: "********",
  lastChangePassword: "",
  googleId: "",
};

const MODAL_CONFIG = {
  BASE: {
    open: false,
    x: 0,
    y: 0,
    width: 460,
    pin: false,
    mode: "normal",
    modalName: "create",
  },
  HEIGHTS: {
    EMAIL: 360,
    RESET_PASSWORD: 280,
    SUCCESS: 350,
  },
};

const MODAL_STATES = {
  EMAIL: { ...MODAL_CONFIG.BASE, height: MODAL_CONFIG.HEIGHTS.EMAIL },
  RESET_PASSWORD: {
    ...MODAL_CONFIG.BASE,
    height: MODAL_CONFIG.HEIGHTS.RESET_PASSWORD,
  },
  SUCCESS: { ...MODAL_CONFIG.BASE, height: MODAL_CONFIG.HEIGHTS.SUCCESS },
};

const validationSchema = yup.object().shape({
  prefix: yup.string().required("กรุณาเลือกคำนำหน้า"),
  customerName: yup.string().required("กรุณากรอกชื่อ"),
  customerSurname: yup.string().required("กรุณากรอกนามสกุล"),
  customerPhoneNumber: yup
    .string()
    .required("กรุณากรอกเบอร์โทรศัพท์")
    .matches(/^\d+$/, "กรุณากรอกเฉพาะตัวเลขเท่านั้น")
    .length(10, "เบอร์โทรศัพท์ต้องมี 10 หลัก"),
  customerGroup: yup.string().required("กรุณาเลือกกลุ่มลูกค้า"),
});

export function CustomerGeneralTab() {
  const dispatch = useDispatch();
  const { customerID } = useParams();
  const prefixNames = useSelector((state) => state.master.prefixNames);
  const businessCustomerGroups = useSelector(
    (state) => state.master.businessCustomerGroups
  );
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [isGoogleIdDisabled, setIsGoogleIdDisabled] = useState(true);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [accountData, setAccountData] = useState(INITIAL_ACCOUNT_DATA);
  const [emailModal, setEmailModal] = useState(MODAL_STATES.EMAIL);
  const [resetPasswordModal, setResetPasswordModal] = useState(
    MODAL_STATES.RESET_PASSWORD
  );
  const [resetPasswordSuccessModal, setResetPasswordSuccessModal] = useState(
    MODAL_STATES.SUCCESS
  );
  const [emailSuccessModal, setEmailSuccessModal] = useState(
    MODAL_STATES.SUCCESS
  );

  const prefixOptions =
    prefixNames?.map((prefix) => ({
      value: prefix.name,
      label: prefix.name,
    })) || [];

  const customerGroupOptions =
    businessCustomerGroups?.map((group) => ({
      value: group.id,
      label: group.name,
    })) || [];

  const fetchCustomerData = useCallback(async () => {
    try {
      if (!customerID) return;

      setIsFormDisabled(true);
      const response = await dispatch(
        customerHandler.getCustomerById(customerID)
      );

      if (response) {
        setFormData({
          prefix: response.prefixName?.name || "",
          customerName: response.first_name || "",
          customerSurname: response.last_name || "",
          customerPhoneNumber: response.phone_number || "",
          customerGroup: response.userBusinessCustomerGroup?.id || "",
        });
        setAccountData({
          email: response.email || "",
          password: "********",
          lastChangePassword: response.password_updated_at
            ? dayjs(response.password_updated_at).format("DD/MM/YYYY")
            : "",
          googleId: response.google_id || "",
        });
      }
    } catch (error) {
      console.error("Error fetching customer data:", error);
    }
  }, [customerID, dispatch]);

  useEffect(() => {
    dispatch(masterHandler.getPrefixNames("NATURAL"));
    dispatch(masterHandler.getBusinessCustomerGroups());
    fetchCustomerData();
  }, [dispatch, customerID, fetchCustomerData]);

  const handleFormSubmit = async (values) => {
    try {
      const prefixId = prefixNames.find((p) => p.name === values.prefix)?.id;
      if (!prefixId) throw new Error("กรุณาเลือกคำนำหน้า");

      const submitData = {
        prefixNameId: Number(prefixId),
        firstName: values.customerName.trim(),
        lastName: values.customerSurname.trim(),
        phoneNumber: values.customerPhoneNumber.trim(),
        userBusinessCustomerGroupId: Number(values.customerGroup),
      };

      console.log(submitData);
      Object.entries(submitData).forEach(([key, value]) => {
        if (!value) throw new Error(`กรุณากรอก${key}`);
      });

      const response = await dispatch(
        customerHandler.updateCustomer(customerID, submitData)
      );

      if (response) {
        setIsFormDisabled(true);
        fetchCustomerData();
      }
    } catch (error) {
      console.error("Error updating customer:", error);
    }
  };

  const handleGoogleIdUpdate = async () => {
    try {
      const response = await dispatch(
        customerHandler.updateCustomerGoogleId(
          customerID,
          accountData.googleId?.trim() || ""
        )
      );

      if (response) {
        setIsGoogleIdDisabled(true);
        fetchCustomerData();
      }
    } catch (error) {
      console.error("Error updating Google ID:", error);
    }
  };

  const renderAccountActions = () => {
    if (!customerID) return null;
    return (
      <>
        <div className="mr-4">
          <Button
            type="button"
            className={`btn-st-3 ${BUTTON_STYLES.changeEmail}`}
            variant="contained"
            onClick={() => setEmailModal((prev) => ({ ...prev, open: true }))}
          >
            เปลี่ยนอีเมล
          </Button>
        </div>
        <div className="mr-4">
          <Button
            type="button"
            className={`btn-st-3 ${BUTTON_STYLES.resetPassword}`}
            variant="contained"
            onClick={() =>
              setResetPasswordModal((prev) => ({ ...prev, open: true }))
            }
          >
            รีเซ็ตรหัสผ่าน
          </Button>
        </div>
      </>
    );
  };

  const renderFormActions = (isFormDisabled, handleFormReset) => {
    if (isFormDisabled) {
      return (
        <Button
          variant="contained"
          className={BUTTON_STYLES.edit}
          type="button"
          onClick={() => setIsFormDisabled(false)}
        >
          แก้ไข
        </Button>
      );
    }

    return (
      <>
        <Button
          type="button"
          className={`btn-st-5 me-3 ${BUTTON_STYLES.cancel}`}
          variant="outlined"
          onClick={handleFormReset}
        >
          ยกเลิก
        </Button>
        <Button
          type="submit"
          className={`btn-st-3 ${BUTTON_STYLES.edit}`}
          variant="contained"
        >
          บันทึก
        </Button>
      </>
    );
  };

  return (
    <>
      <Card className="!rounded-lg mb-4">
        <div className="p-4">
          <div>
            <div className="text-[#4C4E64] font-semibold text-xl mb-4">
              ข้อมูลทั่วไป
            </div>
            <Formik
              enableReinitialize
              initialValues={formData}
              validationSchema={validationSchema}
              onSubmit={handleFormSubmit}
            >
              {({
                handleSubmit,
                handleChange,
                touched,
                errors,
                values,
                setFieldValue,
              }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  <div className="grid grid-cols-12 gap-4">
                    <div className="md:col-span-2 col-span-6">
                      <TextField
                        select
                        label="คำนำหน้า"
                        {...TEXTFIELD_COMMON_PROPS}
                        disabled={isFormDisabled}
                        onChange={handleChange}
                        name="prefix"
                        error={touched.prefix && !!errors.prefix}
                        helperText={touched.prefix && errors.prefix}
                        value={values.prefix}
                      >
                        {prefixOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                    <div className="md:col-span-3 col-span-6">
                      <TextField
                        label="ชื่อ"
                        variant="outlined"
                        {...TEXTFIELD_COMMON_PROPS}
                        disabled={isFormDisabled}
                        onChange={handleChange}
                        name="customerName"
                        value={values.customerName}
                        error={touched.customerName && !!errors.customerName}
                        helperText={touched.customerName && errors.customerName}
                      />
                    </div>
                    <div className="md:col-span-3 col-span-6">
                      <TextField
                        label="นามสกุล"
                        variant="outlined"
                        {...TEXTFIELD_COMMON_PROPS}
                        disabled={isFormDisabled}
                        onChange={handleChange}
                        name="customerSurname"
                        value={values.customerSurname}
                        error={
                          touched.customerSurname && !!errors.customerSurname
                        }
                        helperText={
                          touched.customerSurname && errors.customerSurname
                        }
                      />
                    </div>
                    <div className="md:col-span-2 col-span-6">
                      <TextField
                        label="เบอร์โทร"
                        variant="outlined"
                        {...TEXTFIELD_COMMON_PROPS}
                        InputProps={{
                          ...TEXTFIELD_COMMON_PROPS.InputProps,
                          inputProps: { maxLength: 10 },
                        }}
                        disabled={isFormDisabled}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^\d]/g, "");
                          const truncatedValue = value.slice(0, 10);
                          setFieldValue("customerPhoneNumber", truncatedValue);
                        }}
                        name="customerPhoneNumber"
                        value={values.customerPhoneNumber}
                        error={
                          touched.customerPhoneNumber &&
                          !!errors.customerPhoneNumber
                        }
                        helperText={
                          touched.customerPhoneNumber &&
                          errors.customerPhoneNumber
                        }
                      />
                    </div>
                    <div className="md:col-span-2 col-span-6">
                      <TextField
                        select
                        label="กลุ่มลูกค้า"
                        {...TEXTFIELD_COMMON_PROPS}
                        disabled={isFormDisabled}
                        onChange={(e) => {
                          setFieldValue("customerGroup", e.target.value);
                        }}
                        name="customerGroup"
                        error={touched.customerGroup && !!errors.customerGroup}
                        helperText={
                          touched.customerGroup && errors.customerGroup
                        }
                        value={values.customerGroup}
                      >
                        {customerGroupOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                  </div>

                  <div className="text-end pt-4 btn-group-control">
                    {renderFormActions(isFormDisabled, () =>
                      setIsFormDisabled(true)
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Card>

      <Card className="!rounded-lg mb-4">
        <div className="p-4 mb-4">
          <div className="flex items-center mb-4 pb-2">
            <div className="text-[#4C4E64] font-semibold text-xl mr-7">
              ข้อมูลบัญชี
            </div>
            {renderAccountActions()}
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="md:col-span-6 col-span-6">
              <TextField
                label="อีเมล"
                variant="outlined"
                {...TEXTFIELD_COMMON_PROPS}
                disabled={true}
                value={accountData.email}
              />
            </div>
            <div className="md:col-span-3 col-span-6">
              <TextField
                type="password"
                label="รหัสผ่าน"
                variant="outlined"
                {...TEXTFIELD_COMMON_PROPS}
                disabled={true}
                value={accountData.password}
              />
            </div>
            <div className="md:col-span-3 col-span-6">
              <TextField
                label="วันที่เปลี่ยนรหัสล่าสุด"
                variant="outlined"
                {...TEXTFIELD_COMMON_PROPS}
                InputProps={{
                  ...TEXTFIELD_COMMON_PROPS.InputProps,
                  sx: {
                    ...TEXTFIELD_COMMON_PROPS.InputProps.sx,
                    color: "#FF0000",
                  },
                }}
                disabled={true}
                value={accountData.lastChangePassword}
              />
            </div>
            <div className="col-span-12">
              <div className="flex items-center">
                <TextField
                  {...TEXTFIELD_COMMON_PROPS}
                  label="TOKEN GOOGLE"
                  variant="outlined"
                  disabled={isGoogleIdDisabled}
                  value={accountData.googleId}
                  onChange={(e) =>
                    setAccountData((prev) => ({
                      ...prev,
                      googleId: e.target.value,
                    }))
                  }
                />

                {customerID ? (
                  isGoogleIdDisabled ? (
                    <Button
                      type="button"
                      className="btn-edit-st-1"
                      onClick={() => setIsGoogleIdDisabled(false)}
                    />
                  ) : (
                    <div className="pl-4 w-[120px]">
                      <IconButton
                        aria-label="save"
                        onClick={handleGoogleIdUpdate}
                      >
                        <CheckCircleRoundedIcon className="!text-[#1976d2] !text-[28px]" />
                      </IconButton>
                      <IconButton
                        aria-label="cancel"
                        onClick={() => {
                          setIsGoogleIdDisabled(true);
                          fetchCustomerData();
                        }}
                      >
                        <CancelIcon className="!text-[28px]" />
                      </IconButton>
                    </div>
                  )
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <ChangeEmailModal
        modal={emailModal}
        setModal={setEmailModal}
        setModalEmailSuccess={setEmailSuccessModal}
        formaccount={accountData}
        customerID={customerID}
        refreshData={fetchCustomerData}
      />

      <ResetPasswordModal
        modal={resetPasswordModal}
        setModal={setResetPasswordModal}
        setModalResetPasswordSuccess={setResetPasswordSuccessModal}
        customerID={customerID}
        refreshData={fetchCustomerData}
      />

      <ResetPasswordSuccessModal
        modal={resetPasswordSuccessModal}
        setModal={setResetPasswordSuccessModal}
      />

      <ChangeEmailSuccessModal
        modal={emailSuccessModal}
        setModal={setEmailSuccessModal}
      />
    </>
  );
}
